/**
 * Compact list of tasks that have slipped past the current phase's
 * `plannedEndDate`. Keeps the visual weight down — this section is a
 * warning indicator, not a detailed task list — and links each row to
 * the task's full detail panel via the migration-task list page.
 */
import { Link } from 'react-router-dom';
import type { AssigneeDoc } from '@mct/shared';
import type { OverdueTaskRow } from '../../lib/dashboard';
import { AssigneeChip } from '../common/AssigneeChip';
import { StatusBadge } from '../StatusBadge';

export interface OverdueTaskListProps {
  rows: readonly OverdueTaskRow[];
  assignees: readonly AssigneeDoc[];
  projectId: string;
  /** Cap the visible rows; remainder rendered as "+N 件". Defaults to 8. */
  limit?: number;
}

export function OverdueTaskList({
  rows,
  assignees,
  projectId,
  limit = 8,
}: OverdueTaskListProps): JSX.Element {
  const assigneeById = new Map<string, AssigneeDoc>();
  for (const a of assignees) assigneeById.set(a.assigneeId, a);
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-4 text-sm text-emerald-700">
        遅延しているタスクはありません
      </div>
    );
  }
  const visible = rows.slice(0, limit);
  const hidden = rows.length - visible.length;
  return (
    <ul className="m-0 divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white p-0">
      {visible.map((row) => {
        const assignee = row.assigneeId ? assigneeById.get(row.assigneeId) ?? null : null;
        return (
          <li key={row.taskId} className="flex items-center gap-3 px-3 py-2 text-sm">
            <Link
              to={`/projects/${projectId}/list`}
              className="min-w-0 flex-1 truncate text-slate-800 hover:text-slate-900 hover:underline"
              title={row.name}
            >
              {row.name}
            </Link>
            <StatusBadge status={row.status} className="shrink-0" />
            <AssigneeChip assignee={assignee} className="shrink-0" />
            <span className="shrink-0 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-medium text-rose-700 tabular-nums">
              {row.daysOverdue} 日遅延
            </span>
          </li>
        );
      })}
      {hidden > 0 ? (
        <li className="px-3 py-2 text-[11px] text-slate-500">
          ほか {hidden} 件
        </li>
      ) : null}
    </ul>
  );
}
